import React, { useEffect, useState } from "react";
import { formatMoney } from '../../../common/money';
import "./PromoSection.css";
import axiosInstance from "../../../axiosInstance/axiosInstance";

// Mirror the backend normalization (pkg/promotion/redeem.go): uppercase, strip spaces/dashes
const normalizeCode = (code) => (code || "").toUpperCase().replace(/[\s-]/g, "");

// Mirror the backend discount math (pkg/promotion/redeem.go ApplyOffer).
// Returns the discount AMOUNT (rupees off), or 0 when below the min order.
export const computeDiscount = (offer, orderAmount) => {
    const minOrder = offer.min_order_amount || 0;
    if (orderAmount < minOrder) return 0;

    let discounted;
    if (offer.offer_type === "PERCENT_OFF") {
        discounted = orderAmount * (1 - offer.offer_value / 100);
    } else {
        discounted = orderAmount - offer.offer_value;
    }
    // clamp to [0, orderAmount]
    discounted = Math.max(0, Math.min(discounted, orderAmount));
    // Deliberately NOT rounded. pkg/promotion.ApplyOffer returns this figure unrounded and
    // stores it on the guest request, so rounding here would quote the guest a discount the
    // backend never applies -- 15% of 770 is 115.50, and rounding it to 116 shifted both the
    // discount and the GST derived from it.
    return orderAmount - discounted;
};

const offerLabel = (offer) =>
    offer.offer_type === "PERCENT_OFF"
        ? `${offer.offer_value}% OFF`
        : `₹${offer.offer_value} OFF`;

/**
 * Reusable coupon UI for any promo-eligible service.
 *
 * previewDiscount = true  (immediate-price services: food, spa, restaurant)
 *   The order amount is known now, so we compute + preview the discount and
 *   enforce the min-order locally.
 * previewDiscount = false (quoted-later services: cab, early check-in, extend stay)
 *   No price exists at creation time — the hotel quotes it later — so we just
 *   attach the code. No live discount, no min-order lock.
 */
export default function PromoSection({
    userId,
    hotelId,
    service,
    orderAmount = 0,
    appliedPromo,
    discountAmount,
    onApply,
    onRemove,
    previewDiscount = true,
}) {
    const [offers, setOffers] = useState([]);
    const [code, setCode] = useState("");
    const [error, setError] = useState("");
    const [justApplied, setJustApplied] = useState(false);

    // Fetch the guest's eligible offers and keep only the ones for this service.
    // Scoped to the hotel: a promo from another hotel can never be redeemed here,
    // so listing it would only produce an "invalid promo code" on apply.
    useEffect(() => {
        let mounted = true;
        const fetchOffers = async () => {
            try {
                const res = await axiosInstance.get(`/api/v1/users/${userId}/offers?hotel_id=${hotelId}`);
                const all = res?.data?.data || [];
                if (mounted) setOffers(all.filter((o) => o.service === service));
            } catch (err) {
                // Promo is optional — fail quietly and just hide the offers list.
                console.log("Failed to load offers", err);
            }
        };
        if (userId && hotelId) fetchOffers();
        return () => {
            mounted = false;
        };
    }, [userId, hotelId, service]);

    const applyOffer = (offer) => {
        setError("");
        if (previewDiscount) {
            const minOrder = offer.min_order_amount || 0;
            if (orderAmount < minOrder) {
                const diff = Math.ceil(minOrder - orderAmount);
                setError(`Add ₹${diff} more to use ${offer.code}`);
                return;
            }
        }
        // In attach mode there is no order amount yet, so discount is 0 (computed later by the hotel).
        const discount = previewDiscount ? computeDiscount(offer, orderAmount) : 0;
        onApply(offer, discount);
        setCode("");
        setJustApplied(true);
        setTimeout(() => setJustApplied(false), 1600);
    };

    const handleApplyByCode = () => {
        setError("");
        const normalized = normalizeCode(code);
        if (!normalized) {
            setError("Enter a promo code");
            return;
        }
        const match = offers.find((o) => normalizeCode(o.code) === normalized);
        if (!match) {
            setError("Invalid promo code");
            return;
        }
        applyOffer(match);
    };

    // Applied state — success chip with the animated checkmark.
    if (appliedPromo) {
        return (
            <div className="promo-section">
                <div className={`promo-applied ${justApplied ? "promo-applied--pop" : ""}`}>
                    <div className="promo-applied-check">✓</div>
                    <div className="promo-applied-info">
                        <p className="promo-applied-code">'{appliedPromo.code}' applied</p>
                        <p className="promo-applied-saved">
                            {previewDiscount
                                ? `You saved ₹${formatMoney(discountAmount)} 🎉`
                                : "Discount applied once the hotel confirms your price 🎉"}
                        </p>
                    </div>
                    <button className="promo-remove" onClick={onRemove}>
                        Remove
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="promo-section">
            <h5 className="promo-title">Apply Coupon</h5>
            <div className="promo-input-row">
                <input
                    type="text"
                    placeholder="Enter promo code"
                    value={code}
                    onChange={(e) => {
                        setCode(e.target.value);
                        setError("");
                    }}
                />
                <button onClick={handleApplyByCode}>Apply</button>
            </div>
            {error && <p className="error-message">{error}</p>}

            {offers.length > 0 && (
                <div className="promo-offers">
                    <h6 className="promo-offers-title">Available Offers</h6>
                    {offers.map((offer) => {
                        // Lock only makes sense when we know the order amount (preview mode).
                        const locked =
                            previewDiscount && orderAmount < (offer.min_order_amount || 0);
                        return (
                            <div
                                key={offer.promotion_id}
                                className={`promo-offer-card ${
                                    locked ? "promo-offer-card--locked" : ""
                                }`}
                                onClick={() => applyOffer(offer)}
                            >
                                <div className="promo-offer-badge">{offerLabel(offer)}</div>
                                <div className="promo-offer-body">
                                    <p className="promo-offer-code">{offer.code}</p>
                                    {offer.title && (
                                        <p className="promo-offer-desc">{offer.title}</p>
                                    )}
                                    {offer.min_order_amount > 0 && (
                                        <p className="promo-offer-min">
                                            {locked
                                                ? `Add ₹${Math.ceil(
                                                      offer.min_order_amount - orderAmount
                                                  )} more to unlock`
                                                : `On orders above ₹${offer.min_order_amount}`}
                                        </p>
                                    )}
                                </div>
                                {!locked && <span className="promo-offer-apply">APPLY</span>}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
